import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { debounce } from "lodash";
import { useNotification } from "@kyvg/vue3-notification";
import { defineStore } from "pinia";

import OneStep from "@/components/stepsData/OneStep.vue";
import TwoStep from "@/components/stepsData/TwoStep.vue";
import ThreeStep from "@/components/stepsData/ThreeStep.vue";
import FourStep from "@/components/stepsData/FourStep.vue";
import FiveStep from "@/components/stepsData/FiveStep.vue";
import SixStep from "@/components/stepsData/SixStep.vue";
import SevenStep from "@/components/stepsData/SevenStep.vue";
import EightStep from "@/components/stepsData/EightStep.vue";
import NineStep from "@/components/stepsData/NineStep.vue";
import TenStep from "@/components/stepsData/TenStep.vue";
import ElevenStep from "@/components/stepsData/ElevenStep.vue";
import TwelveStep from "@/components/stepsData/TwelveStep.vue";
import { useActionsStrore } from "./actions.store";
import { useNewEstimateStore } from "@/stores/new/estimateStore";

type Step = {
    key: number;
    index: string;
    name: string;
    path: string;
    component: any;
    hideIndex?: boolean;
};

export const useStepsStore = defineStore("steps", () => {
    const router = useRouter();
    const route = useRoute();

    const actionsStore = useActionsStrore();
    const { notify } = useNotification();
    const newEstimateStore = useNewEstimateStore();

    const activeStep = ref<Step | null>(null);
    const activeStepComponentRef = ref<any>(null);

    const steps: Step[] = [
        {
            index: "1",
            key: 1,
            name: "General information",
            path: "/calculation/1",
            component: OneStep,
        },
        {
            index: "2",
            key: 2,
            name: "Partition(s)",
            path: "/calculation/2",
            component: TwoStep,
        },
        {
            index: "3",
            key: 3,
            name: "Door(s)",
            path: "/calculation/3",
            component: ThreeStep,
        },
        {
            index: "4",
            key: 4,
            name: "Transom(s) and filler(s)",
            path: "/calculation/4",
            component: FourStep,
        },
        {
            index: "5",
            key: 5,
            name: "Chanel, tracks, profiles",
            path: "/calculation/5",
            component: FiveStep,
        },
        {
            index: "6",
            key: 6,
            name: "Extra goods",
            path: "/calculation/6",
            component: SixStep,
        },
        {
            index: "7",
            key: 7,
            name: "Partition Q-ty",
            path: "/calculation/7",
            component: SevenStep,
        },
        {
            index: "8",
            key: 8,
            name: "Installation",
            path: "/calculation/8",
            component: EightStep,
        },
        {
            index: "9",
            key: 9,
            name: "Delivery",
            path: "/calculation/9",
            component: NineStep,
        },
        {
            index: "10",
            key: 10,
            name: "Overhead",
            path: "/calculation/10",
            component: TenStep,
        },
        {
            index: "11",
            key: 11,
            name: "Discount and services",
            path: "/calculation/11",
            component: ElevenStep,
        },
        {
            index: "12",
            key: 12,
            name: "Estimate Rewiew",
            path: "/calculation/12",
            component: TwelveStep,
            hideIndex: true,
        },
    ];

    const setActiveStep = (step: Step) => {
        activeStep.value = step;
    };

    const setActiveStepComponent = (component: any) => {
        activeStepComponentRef.value = component;
    };

    const debouncedSaveDraft = debounce(() => {
        newEstimateStore.save("DRAFT");
        notify({
            title: "Save as a draft",
        });
    }, 1500);


    const goToStep = (index: string) => {
        const step = steps.find((step) => step.index === index);

        if (!step) return;

        router.push(step.path);
    };

    const next = async () => {
        try {
            const nextStep = getNextStep();

            if (!nextStep) return;

            const canNext = await beforeNext();

            if (!canNext) return;

            debouncedSaveDraft();
            router.push({ path: nextStep.path, query: route.query });
        } catch (e) {
            console.error("Validation error!");
        }
    };

    const prev = () => {
        const prevStep = getPrevStep();

        if (!prevStep) return;
        debouncedSaveDraft();

        router.push({ path: prevStep.path, query: route.query });
    };

    const restoreStep = (path: string) => {
        const step = getStepByPath(path);

        if (!step) return;

        actionsStore.reloadSummary();
        setActiveStep(step);
    };

    const beforeNext = () => {
        return new Promise((resolve, reject) => {
            const componentInstance = activeStepComponentRef.value;

            if (!componentInstance) return resolve(true);

            const hasValidation =
                componentInstance.hasOwnProperty("validatePage");

            if (!hasValidation) return resolve(true);

            const errorMessage = hasValidation
                ? componentInstance.validatePage()
                : null;

            if (errorMessage) {
                alert(
                    errorMessage ||
                    "Validation error\nSome of the required fields are not filled correctly",
                );

                return reject();
            }

            return resolve(true);
        });
    };

    const getStepByPath = (path: string) => {
        const currentIndex = steps.findIndex((step) => step.path === path);

        return steps[currentIndex];
    };

    const getNextStep = () => {
        const currentIndex = steps.findIndex(
            (step) => step.path === activeStep.value?.path,
        );

        if (currentIndex !== -1 && currentIndex < steps.length - 1) {
            return steps[currentIndex + 1];
        }

        return null;
    };

    const getPrevStep = () => {
        const currentIndex = steps.findIndex(
            (step) => step.path === activeStep.value?.path,
        );

        if (currentIndex > 0) {
            return steps[currentIndex - 1];
        }

        return null;
    };

    watch(() => route.path, restoreStep, { immediate: true });

    return {
        steps,
        activeStep,
        beforeNext,
        setActiveStepComponent,
        setActiveStep,
        next,
        prev,
        goToStep,
    };
});
